import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  ArrowLeft, Sparkles, RefreshCw, TrendingUp, TrendingDown,
  DollarSign, Users, BarChart3, PieChart, Activity, Calendar,
  Loader2, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { toast } from 'sonner';

const AIAmbassadorAnalytics = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30d');
  const [analytics, setAnalytics] = useState({
    revenue: { total: 0, byTier: {}, trend: 0 },
    bookings: { total: 0, byStatus: {}, byTier: {}, trend: 0 },
    ambassadors: { topPerformers: [], byBookingCount: [] },
    conversion: { views: 0, bookings: 0, rate: 0 }
  });

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch booking stats - use limit=100 (max allowed)
      const [bookingsRes, pendingRes] = await Promise.all([
        api.get('/admin/booking/list?limit=100'),
        api.get('/admin/booking/pending-approval')
      ]);

      const bookings = bookingsRes.data.bookings || [];
      
      // Calculate revenue
      const paidBookings = bookings.filter(b => b.payment_status === 'paid');
      const totalRevenue = paidBookings.reduce((sum, b) => sum + (b.total_price || 0), 0);
      
      // Revenue by tier
      const revenueByTier = {};
      paidBookings.forEach(b => {
        const tier = b.tier || 'unknown';
        revenueByTier[tier] = (revenueByTier[tier] || 0) + (b.total_price || 0);
      });
      
      // Bookings by status
      const bookingsByStatus = {};
      bookings.forEach(b => {
        const status = b.status || 'unknown';
        bookingsByStatus[status] = (bookingsByStatus[status] || 0) + 1;
      });
      
      // Bookings by tier
      const bookingsByTier = {};
      bookings.forEach(b => {
        const tier = b.tier || 'unknown';
        bookingsByTier[tier] = (bookingsByTier[tier] || 0) + 1;
      });
      
      // Top ambassadors by booking count
      const ambassadorBookings = {};
      bookings.forEach(b => {
        const name = b.ambassador?.name || 'Unknown';
        if (!ambassadorBookings[name]) {
          ambassadorBookings[name] = { name, bookings: 0, revenue: 0 };
        }
        ambassadorBookings[name].bookings++;
        if (b.payment_status === 'paid') {
          ambassadorBookings[name].revenue += (b.total_price || 0);
        }
      });
      
      const topPerformers = Object.values(ambassadorBookings)
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 5);
      
      const byBookingCount = Object.values(ambassadorBookings)
        .sort((a, b) => b.bookings - a.bookings)
        .slice(0, 5);

      setAnalytics({
        revenue: {
          total: totalRevenue,
          byTier: revenueByTier,
          trend: 0 // Would need historical data for trend
        },
        bookings: {
          total: bookings.length,
          byStatus: bookingsByStatus,
          byTier: bookingsByTier,
          trend: 0
        },
        ambassadors: {
          topPerformers,
          byBookingCount
        },
        conversion: {
          views: 0, // Would need tracking data
          bookings: bookings.length,
          rate: 0
        }
      });
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const tierColors = {
    standard: 'bg-cyan-100 text-cyan-700',
    premium: 'bg-purple-100 text-purple-700',
    luxury: 'bg-amber-100 text-amber-700',
    custom: 'bg-emerald-100 text-emerald-700'
  };

  const statusColors = {
    pending_approval: 'bg-amber-100 text-amber-700',
    pending_payment: 'bg-blue-100 text-blue-700',
    confirmed: 'bg-green-100 text-green-700',
    active: 'bg-purple-100 text-purple-700',
    completed: 'bg-slate-100 text-slate-700',
    cancelled: 'bg-red-100 text-red-700'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex items-center gap-3">
          <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
          <span className="text-muted-foreground">Loading analytics...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="ai-ambassador-analytics">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/admin/home')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  <BarChart3 className="w-6 h-6 text-purple-500" />
                  AI Ambassador Analytics
                </h1>
                <p className="text-muted-foreground">Performance metrics and revenue insights</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Select value={dateRange} onValueChange={setDateRange}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Select range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7d">Last 7 days</SelectItem>
                  <SelectItem value="30d">Last 30 days</SelectItem>
                  <SelectItem value="90d">Last 90 days</SelectItem>
                  <SelectItem value="all">All time</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={fetchAnalytics} variant="outline">
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {/* Total Revenue */}
          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-sm">Total Revenue</p>
                  <p className="text-3xl font-bold mt-1">{formatCurrency(analytics.revenue.total)}</p>
                  {analytics.revenue.trend !== 0 && (
                    <div className="flex items-center gap-1 mt-2 text-sm">
                      {analytics.revenue.trend > 0 ? (
                        <>
                          <ArrowUpRight className="w-4 h-4" />
                          <span>+{analytics.revenue.trend}% vs last period</span>
                        </>
                      ) : (
                        <>
                          <ArrowDownRight className="w-4 h-4" />
                          <span>{analytics.revenue.trend}% vs last period</span>
                        </>
                      )}
                    </div>
                  )}
                </div>
                <div className="p-3 bg-white/20 rounded-lg">
                  <DollarSign className="w-6 h-6" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Total Bookings */}
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm">Total Bookings</p>
                  <p className="text-3xl font-bold mt-1">{analytics.bookings.total}</p>
                  <p className="text-sm text-blue-100 mt-2">
                    {analytics.bookings.byStatus.confirmed || 0} confirmed
                  </p>
                </div>
                <div className="p-3 bg-white/20 rounded-lg">
                  <Users className="w-6 h-6" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Pending Approvals */}
          <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-amber-100 text-sm">Pending Approval</p>
                  <p className="text-3xl font-bold mt-1">{analytics.bookings.byStatus.pending_approval || 0}</p>
                  <p className="text-sm text-amber-100 mt-2">
                    Requires review
                  </p>
                </div>
                <div className="p-3 bg-white/20 rounded-lg">
                  <Activity className="w-6 h-6" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Avg Order Value */}
          <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-emerald-100 text-sm">Avg. Order Value</p>
                  <p className="text-3xl font-bold mt-1">
                    {analytics.bookings.total > 0 
                      ? formatCurrency(analytics.revenue.total / (Object.values(analytics.bookings.byStatus).reduce((a, b) => a + b, 0) || 1))
                      : '$0'}
                  </p>
                  <p className="text-sm text-emerald-100 mt-2">
                    Per booking
                  </p>
                </div>
                <div className="p-3 bg-white/20 rounded-lg">
                  <TrendingUp className="w-6 h-6" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Revenue by Tier */}
          <Card className="bg-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PieChart className="w-5 h-5 text-purple-500" />
                Revenue by Tier
              </CardTitle>
              <CardDescription>Breakdown of revenue across pricing tiers</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.entries(analytics.revenue.byTier).length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No revenue data yet</p>
                ) : (
                  Object.entries(analytics.revenue.byTier)
                    .sort(([, a], [, b]) => b - a)
                    .map(([tier, amount]) => {
                      const percentage = analytics.revenue.total > 0 
                        ? Math.round((amount / analytics.revenue.total) * 100)
                        : 0;
                      return (
                        <div key={tier} className="space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Badge className={tierColors[tier] || 'bg-slate-100 text-slate-700'}>
                                {tier.charAt(0).toUpperCase() + tier.slice(1)}
                              </Badge>
                              <span className="text-muted-foreground text-sm">{percentage}%</span>
                            </div>
                            <span className="font-semibold">{formatCurrency(amount)}</span>
                          </div>
                          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full ${
                                tier === 'standard' ? 'bg-cyan-500' :
                                tier === 'premium' ? 'bg-purple-500' :
                                tier === 'luxury' ? 'bg-amber-500' :
                                'bg-emerald-500'
                              }`}
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      );
                    })
                )}
              </div>
            </CardContent>
          </Card>

          {/* Bookings by Status */}
          <Card className="bg-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-blue-500" />
                Bookings by Status
              </CardTitle>
              <CardDescription>Current status distribution of all bookings</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.entries(analytics.bookings.byStatus).length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No booking data yet</p>
                ) : (
                  Object.entries(analytics.bookings.byStatus)
                    .sort(([, a], [, b]) => b - a)
                    .map(([status, count]) => {
                      const percentage = analytics.bookings.total > 0 
                        ? Math.round((count / analytics.bookings.total) * 100)
                        : 0;
                      const statusLabel = status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                      return (
                        <div key={status} className="space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Badge className={statusColors[status] || 'bg-slate-100 text-slate-700'}>
                                {statusLabel}
                              </Badge>
                              <span className="text-muted-foreground text-sm">{percentage}%</span>
                            </div>
                            <span className="font-semibold">{count}</span>
                          </div>
                          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-blue-500 rounded-full"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      );
                    })
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Top Performers */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Ambassadors by Revenue */}
          <Card className="bg-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-amber-500" />
                Top Ambassadors by Revenue
              </CardTitle>
              <CardDescription>Highest earning AI Ambassadors</CardDescription>
            </CardHeader>
            <CardContent>
              {analytics.ambassadors.topPerformers.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No data yet</p>
              ) : (
                <div className="space-y-3">
                  {analytics.ambassadors.topPerformers.map((ambassador, index) => (
                    <div 
                      key={ambassador.name}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                          index === 0 ? 'bg-amber-100 text-amber-700' :
                          index === 1 ? 'bg-slate-200 text-slate-700' :
                          index === 2 ? 'bg-orange-100 text-orange-700' :
                          'bg-slate-100 text-slate-500'
                        }`}>
                          {index + 1}
                        </div>
                        <div>
                          <p className="font-medium">{ambassador.name}</p>
                          <p className="text-xs text-muted-foreground">{ambassador.bookings} bookings</p>
                        </div>
                      </div>
                      <span className="font-bold text-green-600">{formatCurrency(ambassador.revenue)}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Top Ambassadors by Bookings */}
          <Card className="bg-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-500" />
                Most Popular Ambassadors
              </CardTitle>
              <CardDescription>AI Ambassadors with most bookings</CardDescription>
            </CardHeader>
            <CardContent>
              {analytics.ambassadors.byBookingCount.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No data yet</p>
              ) : (
                <div className="space-y-3">
                  {analytics.ambassadors.byBookingCount.map((ambassador, index) => (
                    <div 
                      key={ambassador.name}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                          index === 0 ? 'bg-blue-100 text-blue-700' :
                          index === 1 ? 'bg-slate-200 text-slate-700' :
                          index === 2 ? 'bg-purple-100 text-purple-700' :
                          'bg-slate-100 text-slate-500'
                        }`}>
                          {index + 1}
                        </div>
                        <div>
                          <p className="font-medium">{ambassador.name}</p>
                          <p className="text-xs text-muted-foreground">{formatCurrency(ambassador.revenue)} revenue</p>
                        </div>
                      </div>
                      <span className="font-bold text-blue-600">{ambassador.bookings} bookings</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Bookings by Tier */}
        <Card className="bg-white mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-purple-500" />
              Bookings by Tier
            </CardTitle>
            <CardDescription>Distribution of bookings across pricing tiers</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {['standard', 'premium', 'luxury', 'custom'].map(tier => {
                const count = analytics.bookings.byTier[tier] || 0;
                const percentage = analytics.bookings.total > 0 
                  ? Math.round((count / analytics.bookings.total) * 100)
                  : 0;
                return (
                  <div key={tier} className="p-4 bg-slate-50 rounded-lg text-center">
                    <Badge className={tierColors[tier] || 'bg-slate-100 text-slate-700'}>
                      {tier.charAt(0).toUpperCase() + tier.slice(1)}
                    </Badge>
                    <p className="text-3xl font-bold mt-3">{count}</p>
                    <p className="text-sm text-muted-foreground">{percentage}% of total</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AIAmbassadorAnalytics;
